import React from 'react';
import i18n from 'util/i18n';
import { Summary, DateSummary } from 'components/Summary';
import ValidationElement from 'components/Form/ValidationElement';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import {
  CommonOnUpdate,
  CommonOnError,
  CommonScrollIntoView,
  CommonRequired,
  CommonAddressBooks,
} from 'types/props';
import { Dispatch } from 'redux';
import { ErrorCode } from 'redux/modules/application/reducers/error';
import Field from 'components/Form/Field';
import DateControl from 'components/Form/DateControl';
import Textarea from 'components/Form/Textarea';
import Branch from 'components/Form/Branch';
import Show from 'components/Form/Show';
import Accordion from 'components/Form/Accordion';
import Location from 'components/Form/Location';
import PVQLocation from 'components/Form/PVQLocation';
import Text from 'components/Form/Text';
import CheckboxGroup from 'components/Form/CheckboxGroup';
import Checkbox from 'components/Form/Checkbox';
import { LegalCriminalConvictionsItemModel } from 'forms/sections/legal/criminalconviction/types';
import CriminalCharge from './CriminalCharge';

interface Props extends Partial<LegalCriminalConvictionsItemModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
  addressBooks?: CommonAddressBooks
  dispatch?: Dispatch
  defaultState?: boolean
  formType?: string
  errors?: any
}

export default class Conviction extends ValidationElement<Props> {
  update = (queue: Partial<LegalCriminalConvictionsItemModel>) => {
    this.props.onUpdate({

      IsReportedInAnotherSec: this.props.IsReportedInAnotherSec,
      NatureOfAction: this.props.NatureOfAction,
      OffenseDate: this.props.OffenseDate,
      InvolvedViolence: this.props.InvolvedViolence,
      InvolvedFirearms: this.props.InvolvedFirearms,
      InvolvedExplosives: this.props.InvolvedExplosives,
      InvolvedDrugs: this.props.InvolvedDrugs,
      InvolvedAlcohol: this.props.InvolvedAlcohol,
      CaseClosedDate: this.props.CaseClosedDate,
      CaseNotClosed: this.props.CaseNotClosed,
      CaseNotClosedExplanation: this.props.CaseNotClosedExplanation,
      CourtName: this.props.CourtName,
      CourtAddress: this.props.CourtAddress,
      Charges: this.props.Charges,

      ...queue,
    });
  };

  updateIsReportedInAnotherSec = (values: LegalCriminalConvictionsItemModel['IsReportedInAnotherSec']) => {
    this.update({ IsReportedInAnotherSec: values });
  };

  updateNatureOfAction = (values: LegalCriminalConvictionsItemModel['NatureOfAction']) => {
    this.update({ NatureOfAction: values });
  };

  updateOffenseDate = (values: LegalCriminalConvictionsItemModel['OffenseDate']) => {
    this.update({ OffenseDate: values });
  };

  updateInvolvedViolence = (values: LegalCriminalConvictionsItemModel['InvolvedViolence']) => {
    this.update({ InvolvedViolence: values });
  };

  updateInvolvedFirearms = (values: LegalCriminalConvictionsItemModel['InvolvedFirearms']) => {
    this.update({
      InvolvedFirearms: values,
    });
  };

  updateInvolvedExplosives = (values: LegalCriminalConvictionsItemModel['InvolvedExplosives']) => {
    this.update({
      InvolvedExplosives: values,
    });
  };

  updateInvolvedDrugs = (values: LegalCriminalConvictionsItemModel['InvolvedDrugs']) => {
    this.update({
      InvolvedDrugs: values,
    });
  };

  updateInvolvedAlcohol = (values: LegalCriminalConvictionsItemModel['InvolvedAlcohol']) => {
    this.update({
      InvolvedAlcohol: values,
    });
  };

  updateCharges = (values: LegalCriminalConvictionsItemModel['Charges']) => {
    this.update({
      Charges: values,
    });
  };

  updateCaseClosedDate = (values: LegalCriminalConvictionsItemModel['CaseClosedDate']) => {
    this.update({
      CaseClosedDate: values,
      CaseNotClosed: { ...this.props.CaseNotClosed, checked: false },
    });
  };

  updateCaseNotClosed = (values: LegalCriminalConvictionsItemModel['CaseNotClosed']) => {
    this.update({
      CaseNotClosed: values,
      CaseNotClosedExplanation: values?.checked ? this.props.CaseNotClosedExplanation : {},
      CaseClosedDate: values?.checked ? {} : this.props.CaseClosedDate,
    });
  };

  updateCaseNotClosedExplanation = (values: LegalCriminalConvictionsItemModel['CaseNotClosedExplanation']) => {
    this.update({
      CaseNotClosedExplanation: values,
    });
  };

  updateCourtName = (values: LegalCriminalConvictionsItemModel['CourtName']) => {
    this.update({
      CourtName: values,
    });
  };

  updateCourtAddress = (values: LegalCriminalConvictionsItemModel['CourtAddress']) => {
    this.update({
      CourtAddress: values,
    });
  };

  summary = (item, index) => {
    const itemProperties = (item || {}).Item || {};
    const charge = itemProperties.CourtCharge && itemProperties.CourtCharge.value;
    const date = DateSummary(itemProperties.CourtDate);

    return Summary({
      type: i18n.t('legal.police.collection.summary.item'),
      index,
      left: charge,
      right: date,
      placeholder: i18n.t('legal.police.collection.summary.unknown'),
    });
  };

  render() {
    const {
      errors,
    } = this.props;

    const chargesErrors = errors && errors.filter((e) => e.indexOf('Charges.accordion') > -1);
    const attribute = `List.items[${this.props.$index}].Item`;
    const prefix = 'legal.pvq.criminalConvictions.hasCriminalConvictions.items';

    return (
      <div className="criminalconviction">

        {/* Did you report this conviction in another section of this form? */}
        <Branch
          {...this.props.IsReportedInAnotherSec}
          attribute={`${attribute}.IsReportedInAnotherSec`}
          name="IsReportedInAnotherSec"
          label={i18n.t(`${prefix}.isReportedInAnotherSec`)}
          labelSize="h4"
          onUpdate={this.updateIsReportedInAnotherSec}
          onError={this.props.onError}
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
        />

        {this.props.IsReportedInAnotherSec?.value === 'No' && (
          <>
            <Field
              title={i18n.t(`${prefix}.natureOfAction`)} // Nature of the Offense
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.NatureOfAction`}
            >
              <Textarea
                {...this.props.NatureOfAction}
                attribute={`${attribute}.NatureOfAction`}
                name="NatureOfAction"
                className="nature-of-action"
                onUpdate={this.updateNatureOfAction}
                onError={this.props.onError}
                required={this.props.required}
              />
            </Field>

            <Field
              title={i18n.t(`${prefix}.offenseDate`)} // When did the offense happen
              help="legal.nonCriminalAction.help.civilActionDate"
              adjustFor="datecontrol"
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.OffenseDate`}
            >
              <DateControl
                {...this.props.OffenseDate}
                attribute={`${attribute}.OffenseDate`}
                name="OffenseDate"
                className="offense-date"
                minDateEqualTo
                onUpdate={this.updateOffenseDate}
                onError={this.props.onError}
                required={this.props.required}
                hideDay
              />
            </Field>

            <Field>

              <Branch
                {...this.props.InvolvedViolence}
                name="involved_violence"
                label={i18n.t(`${prefix}.violence`)}
                labelSize="h4"
                className="convictions-violence no-margin-bottom"

                onUpdate={this.updateInvolvedViolence}
                required={this.props.required}
                onError={this.props.onError}
                scrollIntoView={this.props.scrollIntoView}
                attribute={`${attribute}.InvolvedViolence`}
              />

              <Branch
                {...this.props.InvolvedFirearms}
                name="involved_firearms"
                label={i18n.t(`${prefix}.firearms`)}
                className="convictions-firearms no-margin-bottom"
                labelSize="h4"
                onUpdate={this.updateInvolvedFirearms}
                required={this.props.required}
                onError={this.props.onError}
                scrollIntoView={this.props.scrollIntoView}
                attribute={`${attribute}.InvolvedFirearms`}
              />

              <Branch
                {...this.props.InvolvedExplosives}
                name="involved_explosives"
                className="convictions-explosives"
                label={i18n.t(`${prefix}.explosives`)}
                labelSize="h4"
                onUpdate={this.updateInvolvedExplosives}
                required={this.props.required}
                onError={this.props.onError}
                scrollIntoView={this.props.scrollIntoView}
                attribute={`${attribute}.InvolvedExplosives`}
              />

              <Branch
                {...this.props.InvolvedDrugs}
                name="involved_drugs"
                className="convictions-drugs"
                label={i18n.t(`${prefix}.drugs`)}
                labelSize="h4"
                onUpdate={this.updateInvolvedDrugs}
                required={this.props.required}
                onError={this.props.onError}
                scrollIntoView={this.props.scrollIntoView}
                attribute={`${attribute}.InvolvedDrugs`}
              />

              <Branch
                {...this.props.InvolvedAlcohol}
                name="involved_alcohol"
                className="convictions-alcohol"
                label={i18n.t(`${prefix}.alcohol`)}
                labelSize="h4"
                onUpdate={this.updateInvolvedAlcohol}
                required={this.props.required}
                onError={this.props.onError}
                scrollIntoView={this.props.scrollIntoView}
                attribute={`${attribute}.InvolvedAlcohol`}
              />

            </Field>
            <div style={{ marginBottom: '8rem' }}>

              <Accordion
                {...this.props.Charges}
                className="criminalconviction-charges"
                defaultState={this.props.defaultState}
                onUpdate={this.updateCharges}
                onError={this.props.onError}
                summary={this.summary}
                description={i18n.t('legal.pvq.police.collection.summary.title')}
                required={this.props.required}
                scrollIntoView={this.props.scrollIntoView}
                errors={chargesErrors}
                attribute={`${attribute}.Charges`}
                ignoreInitialScrollTo
                appendTitle={i18n.t(`${prefix}.charges`)} // Another charge for this offense to report
              >
                <CriminalCharge

                  name="Item"
                  bind
                  dispatch={this.props.dispatch}
                  required={this.props.required}
                  scrollIntoView={this.props.scrollIntoView}
                  offenseDate={this.props.Date}
                  formType={this.props.formType}
                  attribute={`${attribute}.Charges`}
                />

              </Accordion>
            </div>

            <Field
              title={i18n.t(`${prefix}.caseClosedDate`)} // 'When date was your case closed?',
              adjustFor="labels"
              shrink
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.CaseClosedDate`}
            >
              <DateControl
                {...this.props.CaseClosedDate}
                name="CaseClosedDate"
                className="case-closed-date"
                minDateEqualTo
                onUpdate={this.updateCaseClosedDate}
                onError={this.props.onError}
                required={this.props.required}
                hideDay
                attribute={`${attribute}.CaseClosedDate`}
                disabled={this.props.CaseNotClosed?.checked}

              />

              <Checkbox
                label={i18n.t(`${prefix}.caseNotClosed`)} // Checkbox for "Not Closed"
                // value="Not Closed"
                checked={this.props.CaseNotClosed?.checked}
                className="case-not-closed"
                onUpdate={this.updateCaseNotClosed}
                onError={this.props.onError}
              />
            </Field>
            {this.props.CaseNotClosed?.checked === true && ( // Please Explain

              <Field
                title={i18n.t(`${prefix}.caseNotClosedExplanation`)}
                titleSize="h4"
                adjustFor="labels"
                scrollIntoView={this.props.scrollIntoView}
                attribute={`${attribute}.caseNotClosedExplanation`}
              >
                <Textarea
                  {...this.props.CaseNotClosedExplanation}
                  className="case-not-closed-explanation"
                  name="CaseNotClosedExplanation"
                  onError={this.props.onError}
                  required={this.props.required}
                  onUpdate={this.updateCaseNotClosedExplanation}
                  attribute={`${attribute}.CaseNotClosedExplanation`}
                />
              </Field>
            )}

            <Field
              title={i18n.t(`${prefix}.courtName`)}
              adjustFor="labels"
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.CourtName`}
            >
              <Text
                {...this.props.CourtName}
                name="CourtName"
                className="offense-courtname"
                onUpdate={this.updateCourtName}
                onError={this.props.onError}
                required={this.props.required}
                attribute={`${attribute}.CourtName`}
              />
            </Field>

            <Field
              optional
              adjustFor="address"
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.CourtAddress`}
            >

              <PVQLocation
                {...this.props.CourtAddress}
                label={i18n.t(`${prefix}.courtAddress`)}
                domesticTitle={i18n.t(`${prefix}.domesticTitle`)}
                foreignTitle={i18n.t(`${prefix}.foreignTitle`)}
                attribute={`${attribute}.CourtAddress`}
                name="CourtAddress"
                className="court-address"
                customLayout={{ domesticFields: ['street', 'city', 'stateZipcode'], internationalFields: ['city', 'country'] }}
                geocode
                addressBooks={this.props.addressBooks}
                addressBook="Court"
                dispatch={this.props.dispatch}
                onUpdate={this.updateCourtAddress}
                onError={this.props.onError}
                required={this.props.required}
              />
            </Field>

          </>
        )}

      </div>

    );
  }
}

Conviction.defaultProps = {

  IsReportedInAnotherSec: {},
  NatureOfAction: {},
  OffenseDate: {},
  InvolvedViolence: {},
  InvolvedFirearms: {},
  InvolvedExplosives: {},
  InvolvedDrugs: {},
  InvolvedAlcohol: {},
  Charges: { items: [] },
  CaseClosedDate: {},
  CaseNotClosed: {},
  CaseNotClosedExplanation: {},
  CourtName: {},
  CourtAddress: {},

  addressBooks: {},
  dispatch: () => { },
  onUpdate: () => { },
  onError: (value, arr: ErrorCode) => arr,
};
